/* lib/wizardStorage.js · progress-save helpers for the SignupWizard.
 *
 * Two-tier persistence model:
 *
 *   1. SAME-DEVICE RESUME (browser-only)
 *      → sessionStorage (60-min TTL, PII stripped)
 *      → Clears on tab close · safe by default · used as a hardened
 *        replacement for the old localStorage scheme.
 *
 *   2. CROSS-DEVICE RESUME (server-backed)
 *      → POST /api/signup/draft → unguessable UUID4 token, 24h TTL
 *      → Token lives in localStorage so a same-device second-visit
 *        the next morning can still pick up. The token itself is just
 *        an opaque id · without it the server payload is unreachable
 *        and auto-expires.
 *      → Full payload (PII inclusive) lives ONLY on the server, which
 *        is HSTS+CSP-hardened and MongoDB-on-private-network.
 *
 * Public API:
 *   loadCachedProgress() / saveProgress() / clearProgress()
 *     · sessionStorage helpers (unchanged contract from v2)
 *   getDraftToken() / setDraftToken() / clearDraftToken()
 *     · localStorage holder for the server draft id
 *   saveDraftToServer(form, step)
 *     · POST upsert · returns the (possibly new) token
 *   loadDraftFromServer(token)
 *     · GET fetch · returns { form, step } or null on 404/expired
 *   deleteDraftFromServer(token)
 *     · DELETE · best-effort; ignores errors
 *   buildResumeUrl(token)
 *     · returns absolute URL with ?resume=TOKEN query for sharing
 */

const WIZARD_STORAGE_KEY = "ofin.signup.wizard.v2";
const WIZARD_TTL_MS = 60 * 60 * 1000; // 60 minutes (same-device)
const DRAFT_TOKEN_KEY = "ofin.signup.wizard.token";

// Fields that are NEVER persisted to browser storage. They MAY be
// persisted to the server-side draft, where they're protected by an
// unguessable token + 24h TTL + private-network MongoDB.
const PII_FIELDS = new Set([
  "firstname", "lastname",
  "email",
  "phonenumber",
  "address", "city",
  "corporatename",
]);

function safeSessionStorage() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch (_) {
    return null;
  }
}

function safeLocalStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch (_) {
    return null;
  }
}

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/* ─── sessionStorage helpers (same-device, PII-stripped) ───────── */
export function loadCachedProgress() {
  const store = safeSessionStorage();
  if (!store) return null;
  try {
    const raw = store.getItem(WIZARD_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > WIZARD_TTL_MS) {
      store.removeItem(WIZARD_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

export function saveProgress(state) {
  const store = safeSessionStorage();
  if (!store) return;
  try {
    const form = state.form || {};
    const safeForm = {};
    for (const [k, v] of Object.entries(form)) {
      if (!PII_FIELDS.has(k)) safeForm[k] = v;
    }
    store.setItem(WIZARD_STORAGE_KEY, JSON.stringify({
      form: safeForm,
      step: state.step,
      googleVerified: !!state.googleVerified,
      savedAt: Date.now(),
    }));
  } catch (_) { /* quota or disabled · ignore */ }
}

export function clearProgress() {
  const store = safeSessionStorage();
  if (!store) return;
  try { store.removeItem(WIZARD_STORAGE_KEY); } catch (_) { /* noop */ }
}

/* ─── Server-draft token holder (localStorage) ─────────────────── */
export function getDraftToken() {
  const store = safeLocalStorage();
  if (!store) return null;
  try { return store.getItem(DRAFT_TOKEN_KEY); } catch (_) { return null; }
}

export function setDraftToken(token) {
  const store = safeLocalStorage();
  if (!store || !token) return;
  try { store.setItem(DRAFT_TOKEN_KEY, token); } catch (_) { /* noop */ }
}

export function clearDraftToken() {
  const store = safeLocalStorage();
  if (!store) return;
  try { store.removeItem(DRAFT_TOKEN_KEY); } catch (_) { /* noop */ }
}

/* ─── Server-draft network helpers ─────────────────────────────── */
export async function saveDraftToServer(form, step) {
  try {
    const token = getDraftToken();
    const r = await fetch(`${BACKEND}/api/signup/draft`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, payload: { form }, step }),
    });
    if (!r.ok) return null;
    const data = await r.json();
    if (data?.token) {
      // Server may issue a fresh token on first save · persist it so
      // every subsequent save upserts the same draft document.
      if (data.token !== token) setDraftToken(data.token);
      return data.token;
    }
  } catch (_) { /* network blip · best-effort */ }
  return null;
}

export async function loadDraftFromServer(token) {
  if (!token || token.length < 16) return null;
  try {
    const r = await fetch(`${BACKEND}/api/signup/draft/${encodeURIComponent(token)}`);
    if (!r.ok) return null;
    const data = await r.json();
    const form = data?.payload?.form;
    if (!form || typeof form !== "object") return null;
    return { form, step: Number(data.step) || 1, token: data.token };
  } catch (_) { return null; }
}

export async function deleteDraftFromServer(token) {
  if (!token || token.length < 16) return;
  try {
    await fetch(`${BACKEND}/api/signup/draft/${encodeURIComponent(token)}`, { method: "DELETE" });
  } catch (_) { /* noop */ }
}

/* ─── Sharable resume URL ──────────────────────────────────────── */
export function buildResumeUrl(token) {
  if (!token) return "";
  if (typeof window === "undefined") return "";
  const origin = window.location.origin || "";
  return `${origin}/signup?resume=${encodeURIComponent(token)}`;
}
